/* eslint-disable jsx-a11y/click-events-have-key-events */

/* eslint-disable jsx-a11y/no-static-element-interactions */
import 'components/common/date-range-picker/styles/default.css'
import 'components/common/date-range-picker/styles/calendar.css'
import styles from './index.module.css'
import { format, isAfter, isBefore, isValid } from 'date-fns'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import Icon from 'components/ui/icon'
import TextField from 'components/ui/text-field'
import { useCalendar } from './hooks'
import { dateToString, stringToDate } from './utils'
const DEFAULT_DISPLAY_FORMAT = 'MMM d, yyyy'
const DEFAULT_DATA_FORMAT = 'yyyy-MM-dd'
type Props = {
  value?: string | null | undefined
  // SUB-1167: cleared dates report `null`, not `undefined`, so callers that
  // serialise the value into a request body still send the field. `undefined`
  // is dropped by `JSON.stringify`, which reads server-side as "unchanged".
  onChange: (value?: string | null) => void
  onError?: (message: string) => void
  compact?: boolean
  disabled?: boolean
  error?: string | null | undefined
  minDate?: Date
  maxDate?: Date
  label?: React.ReactNode
  displayFormat?: string
  dataFormat?: string
  placeholder?: string
}

/**
 * A date picker component with calendar popup, keyboard input, and date validation
 * @param value - The current date value as a string in the specified data format
 * @param onChange - Callback function called when date value changes, receives new date string
 * @param onError - Optional callback function called when validation errors occur
 * @param compact - Whether to use compact styling - defaults to false
 * @param disabled - Whether the date picker is disabled - defaults to false
 * @param error - Optional error message to display
 * @param minDate - Optional minimum selectable date
 * @param maxDate - Optional maximum selectable date
 * @param label - Optional label to display above the input
 * @param displayFormat - Format for displaying dates to user - defaults to 'MMM d, yyyy'
 * @param dataFormat - Format for internal date string values - defaults to 'yyyy-MM-dd'
 * @param placeholder - Placeholder text for the input field - defaults to 'Select a date'
 * @returns A div containing the date input field with calendar icon and popup calendar
 */
const DatePicker = ({
  value,
  onChange,
  onError,
  compact,
  disabled,
  error,
  minDate,
  maxDate,
  label,
  displayFormat = DEFAULT_DISPLAY_FORMAT,
  dataFormat = DEFAULT_DATA_FORMAT,
  placeholder = 'Select a date',
}: Props) => {
  const [validationError, setValidationError] = useState<
    string | null | undefined
  >()
  const inputRef = useRef<HTMLInputElement | null>(null)
  // Convert date string to date
  const date = stringToDate(value, dataFormat)
  const handleCalendarChange = useCallback(
    (value?: Date) => {
      onChange(dateToString(value, dataFormat) || null)
    },
    [onChange, dataFormat],
  )
  const calendar = useCalendar(
    inputRef,
    date,
    handleCalendarChange,
    minDate,
    maxDate,
  )
  const [inputValue, setInputValue] = useState<string>('')
  const setDate = useCallback(
    (date?: Date) => {
      onChange(date ? format(date, dataFormat) : null)
      calendar.hide()
    },
    [onChange, dataFormat, calendar],
  )
  const handleBlur = useCallback(() => {
    if (!calendar.focused.current) {
      calendar.hide()

      if (!inputValue) {
        // Clear the date if empty, leave it to the parent to handle and set a
        // default if needed. Only report the clear when there was a value to
        // clear, so tabbing through an empty field doesn't dirty the parent.
        if (value) setDate(undefined)
      } else {
        // Try to parse the date as display format
        let parsedDate = stringToDate(inputValue, displayFormat)

        if (!parsedDate || !isValid(parsedDate)) {
          // Try to parse the date as data format
          parsedDate = stringToDate(inputValue, DEFAULT_DATA_FORMAT)
        }

        // Check if the date is valid
        if (parsedDate && isValid(parsedDate)) {
          // Check if the date is within the min and max date
          if (minDate && isBefore(parsedDate, minDate)) {
            // Clamp the date to the min date
            parsedDate = minDate
          }

          if (maxDate && isAfter(parsedDate, maxDate)) {
            // Clamp the date to the max date
            parsedDate = maxDate
          }

          // Set the date
          setDate(parsedDate)
          setInputValue(dateToString(parsedDate, displayFormat) || '')
        } else {
          // Reset the date input to the last valid date
          setInputValue(value || '')
        }
      }
    }
  }, [calendar, inputValue, setDate, displayFormat, minDate, maxDate, value])
  const handleClick = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }

    calendar.show()
  }, [calendar])
  const handleFocus = useCallback(() => {
    calendar.show()
  }, [calendar])
  useEffect(() => {
    const valueAsDate = stringToDate(value, dataFormat)

    if (value) {
      if (isValid(valueAsDate)) {
        setValidationError(undefined)
        // Convert date string to display format
        setInputValue(dateToString(valueAsDate, displayFormat) || '')
      } else {
        setValidationError(
          'This release date is invalid. Please enter a valid release date.',
        )
        // Show the invalid date as is
        setInputValue(value)
        // Alert the parent of the invalid date
        if (onError) onError(status)
      }
    }
  }, [dataFormat, displayFormat, onError, value])
  return (
    <div className={styles.datePickerWrapper} onClick={handleClick}>
      <div>
        <TextField
          compact={compact}
          disabled={disabled}
          error={error || validationError}
          inputRef={inputRef}
          label={label}
          nodeRight={<Icon type="calendar" />}
          placeholder={placeholder}
          value={inputValue}
          warning={!!error}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onChange={event => {
            setInputValue(event.target.value)
          }}
        />
      </div>
      {!disabled && calendar.render()}
    </div>
  )
}

export default React.memo<Props>(DatePicker)
